import { useEffect, useMemo, useRef, useState } from 'react'
import type { Block, ModelOption } from './domain'

type ModelTreeGroup = { group: string; items: { model: ModelOption; index: number }[] }

function groupModels(models: ModelOption[]): ModelTreeGroup[] {
  const byGroup = new Map<string, { model: ModelOption; index: number }[]>()
  models.forEach((model, index) => {
    const group = model.model_group ?? 'Other'
    if (!byGroup.has(group)) byGroup.set(group, [])
    byGroup.get(group)!.push({ model, index })
  })
  return [...byGroup.entries()]
    .map(([group, items]) => ({
      group,
      items: items.slice().sort((a, b) => a.model.label.localeCompare(b.model.label)),
    }))
    .sort((a, b) => a.group.localeCompare(b.group))
}

export function ModelSelect({ models, value, onChange }: {
  models: ModelOption[]
  value: number
  onChange: (index: number) => void
}) {
  const [open, setOpen] = useState(false)
  const [queryText, setQueryText] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const ref = useRef<HTMLDivElement>(null)
  const groups = useMemo(() => groupModels(models), [models])
  const close = () => { setOpen(false); setQueryText(''); setExpanded(new Set()) }

  useEffect(() => {
    if (!open) return
    const onDocumentClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) close()
    }
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') close() }
    document.addEventListener('mousedown', onDocumentClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocumentClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const query = queryText.trim().toLowerCase()
  const filtered = query
    ? groups.map(group => ({
        group: group.group,
        items: group.items.filter(({ model }) =>
          model.label.toLowerCase().includes(query) ||
          group.group.toLowerCase().includes(query) ||
          model.codes.some(code => code.toLowerCase().includes(query))),
      })).filter(group => group.items.length > 0)
    : groups
  const selected = models[value]
  const selectedGroup = selected ? (selected.model_group ?? 'Other') : null
  const toggleGroup = (group: string) => setExpanded(current => {
    const next = new Set(current)
    next.has(group) ? next.delete(group) : next.add(group)
    return next
  })
  const openDropdown = () => {
    setOpen(true)
    setExpanded(new Set(selectedGroup ? [selectedGroup] : []))
  }

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => (open ? close() : openDropdown())}
        className="flex w-full items-center justify-between rounded-[4px] border border-[#CFD3DB] bg-white px-3 py-2 text-left text-sm text-black focus:border-bmw-ocean focus:outline-none">
        <span className="truncate">{selected ? selected.label : 'Select model…'}</span>
        <span className="ml-2 flex-shrink-0 text-[#999]">▾</span>
      </button>
      {open && (
        <div className="absolute left-0 right-0 z-30 mt-1 rounded-[4px] border border-[#CFD3DB] bg-white shadow-lg">
          <div className="border-b border-[#E5E7EC] p-2">
            <input autoFocus value={queryText} onChange={event => setQueryText(event.target.value)} placeholder="Search models…"
              className="w-full rounded-[4px] border border-[#E5E7EC] bg-white px-2 py-1.5 text-sm focus:border-bmw-ocean focus:outline-none" />
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {filtered.length === 0 && <p className="px-3 py-2 text-xs text-[#999]">No models match “{queryText}”.</p>}
            {filtered.map(group => {
              const groupOpen = query !== '' || expanded.has(group.group)
              return (
                <div key={group.group}>
                  <button type="button" onClick={() => toggleGroup(group.group)}
                    className={`flex w-full items-center justify-between px-3 py-2 text-left text-[13px] font-bold uppercase tracking-[0.12em] hover:bg-[#FAFBFC] ${group.group === selectedGroup ? 'bg-[#F3F6FB] text-bmw-ocean' : 'text-[#777]'}`}>
                    <span>{group.group}{group.group === selectedGroup ? ' · current' : ''}</span>
                    <span className={group.group === selectedGroup ? 'text-bmw-ocean' : 'text-[#999]'}>{groupOpen ? '▾' : `▸ ${group.items.length}`}</span>
                  </button>
                  {groupOpen && group.items.map(({ model, index }) => (
                    <button key={index} type="button" onClick={() => { onChange(index); close() }}
                      className={`block w-full px-3 py-1.5 pl-5 text-left text-sm hover:bg-[#F3F6FB] ${index === value ? 'bg-[#F3F6FB] font-bold text-bmw-ocean' : 'text-black'}`}>
                      {model.label}
                    </button>
                  ))}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

const CONFIDENCE_STYLES: Record<string, string> = {
  HIGH: 'bg-bmw-ocean text-white',
  MEDIUM: 'border border-[#E0A200] bg-[#FFF6E0] text-[#8A5A00]',
  LOW: 'border border-[#C0392B] bg-[#FDECEA] text-[#B03A2E]',
}

export function confidenceBadge(label: string, score: number) {
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-bold uppercase tracking-[0.06em] ${CONFIDENCE_STYLES[label] ?? 'border border-[#CFD3DB] text-black'}`}>
      {label === 'LOW' && <span aria-hidden>⚠</span>}
      {label} · {(score * 100).toFixed(0)}%
    </span>
  )
}

export function ConfidenceBanner({ label, score, lowBlocks, totalBlocks, noData }: {
  label: string; score: number; lowBlocks: number; totalBlocks: number; noData: boolean
}) {
  if (noData) {
    return (
      <div className="mb-5 border border-[#C0392B] bg-[#FDECEA] px-4 py-3 text-sm text-[#B03A2E]">
        <span className="font-bold">⚠ Insufficient data</span> — too few historical orders to
        predict this model. Treat any specs as manual guesses and review carefully before submitting.
      </div>
    )
  }
  if (label === 'HIGH') return null
  const isLow = label === 'LOW'
  const styles = isLow
    ? 'border-[#C0392B] bg-[#FDECEA] text-[#B03A2E]'
    : 'border-[#CFD3DB] bg-[#F4F6F9] text-[#555]'
  return (
    <div className={`mb-5 border px-4 py-3 text-sm ${styles}`}>
      <span className="font-bold uppercase tracking-[0.06em]">{isLow ? '⚠' : 'ℹ'} {label} confidence · {(score * 100).toFixed(0)}%</span>
      <span className="ml-2">
        {isLow
          ? '— Thin data for this model. Double-check the predicted specs before submitting the order.'
          : `— Moderate data.${lowBlocks > 0 ? ` ${lowBlocks} of ${totalBlocks} spec blocks are lower-confidence — worth a look.` : ''}`}
      </span>
    </div>
  )
}

export function modelConfidence(blocks: Block[]): { score: number; label: 'HIGH' | 'MEDIUM' | 'LOW' } {
  const modelBlocks = blocks.filter(block => block.source !== 'user')
  const weight = modelBlocks.reduce((sum, block) => sum + block.weight_share, 0)
  const score = weight > 0
    ? modelBlocks.reduce((sum, block) => sum + block.overall_confidence * block.weight_share, 0) / weight
    : 0
  return { score, label: score >= 0.65 ? 'HIGH' : score >= 0.35 ? 'MEDIUM' : 'LOW' }
}

export function NumberField({ value, onCommit, min, max, className, title }: {
  value: number
  onCommit: (value: number) => void
  min?: number
  max?: number
  className?: string
  title?: string
}) {
  const [draft, setDraft] = useState<string | null>(null)
  const commit = () => {
    if (draft === null) return
    const parsed = parseInt(draft, 10)
    const clamped = Number.isNaN(parsed)
      ? value
      : Math.max(min ?? -Infinity, Math.min(max ?? Infinity, parsed))
    onCommit(clamped)
    setDraft(null)
  }
  return (
    <input type="number" min={min} max={max} title={title} className={className}
      value={draft ?? String(value)} onChange={event => setDraft(event.target.value)}
      onBlur={commit} onKeyDown={event => { if (event.key === 'Enter') event.currentTarget.blur() }} />
  )
}
